import { motion } from "framer-motion";
import { Sparkles, Users, Building2, Rocket, TrendingUp, Heart } from "lucide-react";

/**
 * Timeline della storia di Colucci Assicuratori.
 *
 * IMPORTANTE: i titoli dei capitoli sono narrativi (non richiedono anni precisi).
 * Se il cliente fornisce date ufficiali, sostituire `period` con l'anno reale.
 */
const MILESTONES = [
  {
    period: "Le origini",
    title: "La nascita dell'agenzia",
    body: "Ciro Colucci fonda l'agenzia a Napoli con un'unica regola: accanto a ogni cliente, sempre. Pochi metri quadrati, tanta pazienza, primo assicurato firmato a mano.",
    icon: Sparkles,
  },
  {
    period: "Anni di consolidamento",
    title: "Il mestiere di ascoltare",
    body: "Famiglie, commercianti e piccoli imprenditori del territorio si affidano a noi. Impariamo che l'assicurazione non e' un prodotto: e' una conversazione che dura nel tempo.",
    icon: Users,
  },
  {
    period: "Crescita territoriale",
    title: "Il team diventa studio",
    body: "Arrivano i primi collaboratori stabili, nascono i rami specializzati (Motor, Vita, Danni, Convenzioni). Le compagnie partner aumentano a doppia cifra: al cliente consegniamo sempre la polizza migliore, non la piu' facile da vendere.",
    icon: Building2,
  },
  {
    period: "Seconda generazione",
    title: "Christian Colucci al timone",
    body: "Con la seconda generazione arriva una nuova sensibilita': processi digitali, customer experience, compliance IVASS senza compromessi. Il rapporto umano resta il cuore, ma le pratiche diventano piu' veloci e trasparenti.",
    icon: Heart,
  },
  {
    period: "Oggi",
    title: "Digitale + Consulenza umana",
    body: "Portale web, questionari guidati, estrazione AI dei documenti, noleggio lungo termine con Ayvens, canale Whistleblowing, editoriale Instagram. Tutto per lasciare piu' tempo al consulente di fare cio' che conta: parlarti.",
    icon: Rocket,
  },
  {
    period: "Domani",
    title: "La prossima sfida",
    body: "Espansione dei servizi di gestione sinistri assistita, 2FA sicurezza, nuove partnership compagnia e formazione continua del team. Ogni evoluzione tecnologica deve servire un solo obiettivo: rendere piu' semplice la tua protezione.",
    icon: TrendingUp,
  },
];

export default function TeamTimeline() {
  return (
    <section className="py-24 md:py-32 bg-[#F5F4F0] relative overflow-hidden" data-testid="team-timeline">
      <div className="absolute inset-0 grain-texture opacity-[0.04] pointer-events-none" />
      <div className="relative max-w-5xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D97B58] mb-4">La Nostra Storia</p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-zinc-900 mb-4">
            Due generazioni, <span className="text-zinc-400">un solo modo di lavorare.</span>
          </h2>
          <p className="text-lg text-zinc-500 max-w-2xl mx-auto">
            Dall'agenzia di quartiere allo studio moderno: i capitoli che hanno reso Colucci un punto di riferimento per chi cerca consulenza vera.
          </p>
        </div>

        <div className="relative">
          {/* Linea verticale centrale (desktop) / sinistra (mobile) */}
          <div className="absolute left-5 md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-[#D97B58]/60 via-[#163321]/40 to-transparent md:-translate-x-1/2" aria-hidden="true" />

          <div className="space-y-12 md:space-y-20">
            {MILESTONES.map((m, i) => {
              const isRight = i % 2 === 1;
              const Icon = m.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.5, delay: 0.08 * i }}
                  className={`relative pl-14 md:pl-0 md:grid md:grid-cols-2 md:gap-12 md:items-center ${isRight ? "md:[&>div:first-child]:order-2" : ""}`}
                  data-testid={`timeline-milestone-${i}`}>
                  {/* Nodo circolare con icona */}
                  <div className={`absolute left-5 md:left-1/2 -translate-x-1/2 top-2 w-10 h-10 rounded-full bg-white border-2 border-[#D97B58] shadow-lg flex items-center justify-center z-10`}>
                    <Icon className="w-4 h-4 text-[#D97B58]" />
                  </div>
                  {/* Card */}
                  <div className={`bg-white rounded-2xl border border-zinc-200 p-6 shadow-sm hover:shadow-lg transition-shadow ${isRight ? "md:mr-16" : "md:ml-16"}`}>
                    <p className="text-[11px] font-bold uppercase tracking-wider text-[#D97B58] mb-2">{m.period}</p>
                    <h3 className="font-heading text-xl font-bold text-zinc-900 mb-2">{m.title}</h3>
                    <p className="text-sm text-zinc-600 leading-relaxed">{m.body}</p>
                  </div>
                  {/* Spaziatore lato opposto (desktop) */}
                  <div className="hidden md:block" aria-hidden="true" />
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
